import { defineStore, type StoreGeneric, storeToRefs } from 'pinia';
import { computed, ref, watch, Ref, markRaw } from 'vue';
import type { Content } from '@ilteducation/content-registry-types';
import { ContentQuery } from './types';
import { getCollectionContent } from './api';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useMyProfiles } from '@ilteducation/profile';

type ContentsState = {
  isLoading: boolean;
  contentQuery: ContentQuery;
  contents: Content[];
};

type State = {
  [collectionId: string]: ContentsState | undefined;
};

const useCollectionContentsStore = defineStore('content-collection-contents', () => {
  const contentsState = ref<State>({});

  const getOrCreateState = (collectionId: string): ContentsState => {
    if (!contentsState.value[collectionId]) {
      contentsState.value[collectionId] = {
        isLoading: false,
        contents: [],
        contentQuery: { cursor: null },
      };
    }
    return contentsState.value[collectionId]!;
  };

  const contents = computed(
    () =>
      (collectionId: Ref<string>): Content[] =>
        contentsState.value[collectionId.value]?.contents ?? [],
  );
  const isLoading = computed(
    () =>
      (collectionId: Ref<string>): boolean =>
        contentsState.value[collectionId.value]?.isLoading ?? false,
  );
  const hasMore = computed(
    () =>
      (collectionId: Ref<string>): boolean =>
        contentsState.value[collectionId.value]?.contentQuery.cursor !== null,
  );

  // This is a known type-checking bug that was present in older versions of Pinia, specifically when using storeToRefs() with a mix of ref,
  // computed, and methods in the setup store syntax. Newer versions of Pinia have fixed these type inference issues,
  // allowing the storeToRefs() helper to correctly preserve the types of your properties.
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  const { current: currentProfile } = useMyProfiles();

  const $reset = () => {
    contentsState.value = {};
  };

  watch(user, () => {
    // reset on identity change
    $reset();
  });

  const loadCollectionContents = async (collectionId: string, contentQuery: ContentQuery): Promise<void> => {
    const stateForId = getOrCreateState(collectionId);

    if (stateForId.isLoading) {
      return;
    }

    stateForId.isLoading = true;
    stateForId.contentQuery = contentQuery;

    try {
      const { cursor, data } = await getCollectionContent(collectionId, contentQuery, currentProfile.value?.id);

      if (data && contentsState.value[collectionId]) {
        const collectionState = contentsState.value[collectionId]!;
        collectionState.isLoading = false;
        collectionState.contentQuery.cursor = cursor;

        // Paging is cursor based, so a list whose order changed under us (a reorder) can hand back
        // items we already hold. Appending those blind is what duplicated rows until a full reload.
        const loaded = new Set(collectionState.contents.map((content) => content.id));
        const nonReactiveData = data.filter((content) => !loaded.has(content.id)).map((content) => markRaw(content));
        collectionState.contents.push(...nonReactiveData);
      }
    } catch (error) {
      if (contentsState.value[collectionId]) {
        const collectionState = contentsState.value[collectionId]!;
        collectionState.isLoading = false;
        collectionState.contentQuery.cursor = null; // TODO future retries?
      }
      throw error;
    }
  };

  const next = async (collectionId: string): Promise<void> => {
    const state = getOrCreateState(collectionId);
    await loadCollectionContents(collectionId, state.contentQuery);
  };

  /**
   * Keeps the loaded pages in the order the server just persisted, so the list does not snap back to
   * the old order on the next render and no refetch is needed.
   */
  const moveContent = (collectionId: string, movedContentId: string, afterContentId?: string): void => {
    const state = contentsState.value[collectionId];
    if (!state) {
      return;
    }
    const from = state.contents.findIndex((content) => content.id === movedContentId);
    if (from < 0) {
      return;
    }
    const [moved] = state.contents.splice(from, 1);
    if (!afterContentId) {
      state.contents.unshift(moved);
      return;
    }
    const after = state.contents.findIndex((content) => content.id === afterContentId);
    // The item it now sits behind may not be loaded yet, in which case the tail is the best guess.
    state.contents.splice(after < 0 ? state.contents.length : after + 1, 0, moved);
  };

  /**
   * Drops removed items from the loaded pages. History and predefined collections carry an empty
   * reference list, so updating the collection alone leaves the item sitting in the list.
   */
  const dropContents = (collectionId: string, contentIds: string[]): void => {
    const state = contentsState.value[collectionId];
    if (!state) {
      return;
    }
    const removed = new Set(contentIds);
    state.contents = state.contents.filter((content) => !removed.has(content.id));
  };

  const resetById = (collectionId: string): void => {
    if (contentsState.value[collectionId]) {
      const collectionState = contentsState.value[collectionId]!;
      collectionState.isLoading = false;
      collectionState.contents = [];
      collectionState.contentQuery.cursor = null;
      next(collectionId);
    }
  };

  return {
    contents,
    isLoading,
    hasMore,
    loadCollectionContents,
    next,
    moveContent,
    dropContents,
    resetById,
    $reset,
  };
});

export default useCollectionContentsStore;
